import React, { useState } from 'react';
import { CreditCard, Download, FileText } from 'lucide-react';
import reportService from '../../services/report/reportService';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/currencyUtils';
import CheckReportFirmCard from './CheckReportFirmCard';
import CheckReportVariantsBanner from './CheckReportVariantsBanner';
import type { CheckReportData } from '../../types/report';

/**
 * R17 Task H (Peter #2a): Check Report by Firm — aggregates manual checks
 * across all salesmen for a date range and groups them by check firm.
 * Layout mirrors ClientReport so both report screens read as one family.
 */
const CheckReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [reportData, setReportData] = useState<CheckReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportService.generateCheckReport({
        start_date: startDate,
        end_date: endDate,
      });
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await reportService.downloadCheckReportPdf({
        start_date: startDate,
        end_date: endDate,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await reportService.downloadCheckReportExcel({
        start_date: startDate,
        end_date: endDate,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <CreditCard className="h-7 w-7 mr-3 text-primary-600" />
          Check Commission Report
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Aggregate manual checks by firm across all salesmen for a date range
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-end gap-3">
            <button
              onClick={handleGenerate}
              disabled={loading || !startDate || !endDate}
              className="flex-1 bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate
            </button>

            {reportData && (
              <>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="flex-1 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingPdf ? 'PDF...' : 'PDF'}
                </button>
                <button
                  onClick={handleDownloadExcel}
                  disabled={downloadingExcel}
                  className="flex-1 bg-emerald-700 text-white px-6 py-2 rounded-lg hover:bg-emerald-800 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloadingExcel ? 'Excel...' : 'Excel'}
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Generating report...</p>
          </div>
        </div>
      )}

      {!loading && reportData && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Period</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(reportData.date_range.start_date)} to {formatDate(reportData.date_range.end_date)}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Firms</p>
                <p className="text-lg font-semibold text-gray-900">{reportData.summary.firm_count}</p>
                <p className="text-xs text-gray-500 mt-0.5">{reportData.summary.check_count} checks</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Total Gross</p>
                <p className="text-lg font-semibold text-green-700">
                  {formatCurrency(reportData.summary.total_gross)}
                </p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Salesman Commission</p>
                <p className="text-lg font-semibold text-blue-700">
                  {formatCurrency(reportData.summary.total_salesman_commission)}
                </p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">House Commission</p>
                <p className="text-lg font-semibold text-amber-700">
                  {formatCurrency(reportData.summary.total_house_commission)}
                </p>
              </div>
            </div>
          </div>

          {/* Firm-name variants data-quality banner */}
          <CheckReportVariantsBanner variants={reportData.firm_name_variants} />

          {/* Per-firm cards */}
          {reportData.firms.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center text-gray-500">
              No manual checks in the selected date range.
            </div>
          ) : (
            reportData.firms.map(firm => (
              <CheckReportFirmCard key={firm.firm_name} firm={firm} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CheckReport;
